import { NetworkErrorCode, WsNetworkError } from '../wsErrors/WsNetworkError';
import { NetworkEventType } from '../wsServices/WsNetworkService';
import { WsRequest } from './WsRequest';
import { WsResponse } from './WsResponse';
import { IWsSpecificNetworkService } from './WsSpecificNetworkService.types';

export class WsRequestManager {
  private responses: WsResponse<unknown>[] = [];

  private unsubscribeRetry: (() => void) | null = null;
  private unsubscribeError: (() => void) | null = null;

  constructor(private networkService: IWsSpecificNetworkService) {}

  request<ExpectedResponseBody>(
    data: unknown,
    retryOnlyOnError?: boolean
  ): Promise<ExpectedResponseBody> {
    const request = new WsRequest(data, retryOnlyOnError);
    const response = new WsResponse<ExpectedResponseBody>(request);

    this.responses.push(response);

    this.sendRequest(request, response);

    return response.promise;
  }

  private sendRequest<ExpectedResponseBody>(
    request: WsRequest,
    response: WsResponse<ExpectedResponseBody>
  ): void {
    this.subscribeRetry();
    this.subscribeError();

    this.networkService.sendRequest(request.message, request, (responseResult) => {
      if (responseResult.error) {
        response.reject(responseResult.error);
      }

      if (responseResult.retry) {
        const newRequest = response.request.clone();
        response.request = newRequest;

        this.sendRequest(newRequest, response);
        return;
      }

      if (responseResult.response) {
        response.resolve(responseResult.response);
      }

      this.checkSubscribersAndClean();
    });
  }

  private subscribeError(): void {
    if (this.unsubscribeError) {
      return;
    }

    this.unsubscribeError = this.networkService.on(NetworkEventType.NetworkError, (error) => {
      this.responses.forEach((response) => {
        if (error instanceof WsNetworkError) {
          if (error.code === NetworkErrorCode.ServiceUnavailable && response.attempts < 3) {
            response.setAttempts(response.attempts + 1);
            return;
          }

          if (error.code === NetworkErrorCode.InternalError && response.attempts < 10) {
            response.setAttempts(response.attempts + 1);
            return;
          }

          response.reject(error);
        }
      });

      this.checkSubscribersAndClean();
    });
  }

  private subscribeRetry(): void {
    if (this.unsubscribeRetry) {
      return;
    }

    this.unsubscribeRetry = this.networkService.on(NetworkEventType.Retry, () => {
      if (!this.responses.length) {
        this.checkSubscribersAndClean();
        return;
      }

      this.responses.forEach((response) => {
        // TODO: temporary fix. (websocket emit OPEN event after message sent). see WebsocketService
        if (response.request.retryOnlyOnError && response.request.isSent) {
          return;
        }

        response.request = response.request.clone();
        this.sendRequest(response.request, response);
      });
    });
  }

  private checkSubscribersAndClean(): void {
    this.responses = this.responses.filter((response) => !response.isCompleted);
    if (this.responses.length) {
      return;
    }

    this.unsubscribeRetry?.();
    this.unsubscribeRetry = null;

    this.unsubscribeError?.();
    this.unsubscribeError = null;
  }
}
